import React, { useEffect, useRef, useState } from "react";
import {
    Typography,
    Stack,
    useTheme,
    ButtonBase,
    Slide,
    Box,
} from "@mui/material";
import { alpha, darken } from "@mui/material/styles";
import AIIcon from "../img/AI.svg";
import ChevronLeft from "../img/Arrow_Left.svg";
import ChevronRight from "../img/Arrow_Right.svg";
import TextField from "./TextField";
import ColoredIcon from "./ColoredIcon";
import Button from "./Button";
import { DisplayMessage } from "./AITextField";
import { APP_MAX_WIDTH } from "../constants/layout";
import AICompletions from "../api/AICompletions";
import { FONTS } from "../constants/styles";
import { useSwipeToDismiss } from "../hooks/useSwipeToDismiss";
import { hideLanguageSwitcher } from "../utils/languageUtils";

interface Props {
    value?: string;
    open: boolean;
    onCancel: () => void;
    onSave: (val?: string) => void;
    displayHistory: DisplayMessage[];
    setDisplayHistory: React.Dispatch<React.SetStateAction<DisplayMessage[]>>;
    businessType: string;
    language?: string;
}

const TextPromptModal = (props: Props) => {
    const {
        open,
        onCancel,
        onSave,
        displayHistory,
        setDisplayHistory,
        businessType,
        language = "English",
    } = props;

    const theme = useTheme();
    const { sheetRef, handleProps } = useSwipeToDismiss(onCancel);

    const [message, setMessage] = useState<string | undefined>();
    const [userMessage, setUserMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const messagesEndRef = useRef<null | HTMLDivElement>(null);
    const scrollDown = () =>
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

    const handleUserMessageSubmit = async () => {
        const trimmedMessage = userMessage.trim();
        if (trimmedMessage.length === 0) return;

        const banList = displayHistory
            .filter((m) => m.role === "assistant")
            .map((m) => (m.content ?? "").toString())
            .filter(Boolean);

        setDisplayHistory((prev) => [
            ...prev,
            { role: "user", content: trimmedMessage },
        ]);
        setLoading(true);

        try {
            const res = await AICompletions.requestRefinedAppName(
                businessType,
                trimmedMessage,
                language,
                banList
            );
            const assistantMsg: DisplayMessage = {
                role: "assistant",
                content: res.name,
            };
            setDisplayHistory((prev) => [...prev, assistantMsg]);
            setMessage(res.name);
        } catch (err) {
            console.error("Generation error:", err);
        } finally {
            setUserMessage("");
            scrollDown();
            setLoading(false);
        }
    };

    // initial generation
    useEffect(() => {
        hideLanguageSwitcher();
        const initialUserDisplay = `Generate a creative app name for my ${businessType} business.`;

        if (displayHistory.length <= 1) {
            setDisplayHistory([{ role: "user", content: initialUserDisplay }]);
            setLoading(true);

            AICompletions.requestInitialAppName(businessType, language)
                .then((res) => {
                    const assistantMsg: DisplayMessage = {
                        role: "assistant",
                        content: res.name,
                    };
                    setDisplayHistory((prev) => [...prev, assistantMsg]);
                    setMessage(res.name);
                })
                .catch((err) => console.error("Initial generation error:", err))
                .finally(() => {
                    scrollDown();
                    setLoading(false);
                });
        } else {
            const lastAssistant = [...displayHistory]
                .reverse()
                .find((m) => m.role === "assistant");
            if (lastAssistant) setMessage(lastAssistant.content);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Slide direction="up" in={open} mountOnEnter unmountOnExit>
            <Box
                ref={sheetRef}
                position="absolute"
                left={0}
                right={0}
                bottom={0}
                height="95%"
                display="flex"
                justifyContent="center"
            >
                <Stack
                    className="force-dynamic-translation"
                    width="100%"
                    maxWidth={APP_MAX_WIDTH}
                    bgcolor="white"
                    borderRadius="14px 14px 0 0"
                    justifyContent="center"
                    alignItems="center"
                >
                    {/* Drag handle */}
                    <Box
                        sx={{
                            width: 36,
                            height: 4,
                            borderRadius: 2,
                            bgcolor: "rgba(0,0,0,0.15)",
                            mt: 1,
                        }}
                    />
                    <Stack
                        {...handleProps}
                        paddingY={1}
                        spacing={2}
                        width="90%"
                        direction="row"
                        alignItems="center"
                        sx={{ touchAction: "none", cursor: "grab" }}
                    >
                        <ButtonBase
                            disabled={loading}
                            onClick={onCancel}
                            sx={{
                                width: "38px",
                                minWidth: "38px",
                                height: "38px",
                            }}
                        >
                            <ColoredIcon
                                icon={ChevronLeft}
                                size="100%"
                                color="black"
                            />
                        </ButtonBase>
                        <Stack
                            justifyContent="center"
                            alignItems="center"
                            width="50px"
                            height="50px"
                            borderRadius="50px"
                            bgcolor={theme.palette.primary.main}
                        >
                            <ColoredIcon
                                icon={AIIcon}
                                color="white"
                                size="34px"
                            />
                        </Stack>
                        <Typography
                            fontFamily={FONTS.display}
                            fontWeight={700}
                            fontSize="20px"
                            lineHeight="30px"
                        >
                            AI Copywriter
                        </Typography>
                    </Stack>
                    <Stack
                        direction="column"
                        height="100%"
                        width="100%"
                        overflow="auto"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                        paddingTop={2}
                        bgcolor={alpha(theme.palette.primary.main, 0.05)}
                    >
                        {displayHistory.map((x, index) => (
                            <MessageBox
                                key={index}
                                message={x}
                                className={
                                    index === 0
                                        ? "force-dynamic-translation"
                                        : ""
                                }
                            />
                        ))}
                        <div ref={messagesEndRef} style={{ marginTop: 10 }} />
                    </Stack>
                    <Stack width="90%" paddingY={2} spacing={2}>
                        <Button
                            disabled={loading}
                            onClick={() => onSave(message)}
                        >
                            Save
                        </Button>
                        <TextField
                            multiline
                            maxRows={5}
                            padding={2}
                            fullWidth
                            variant="outlined"
                            placeholder="Message"
                            value={userMessage}
                            onChange={(e) =>
                                setUserMessage(e.currentTarget.value)
                            }
                            icon={ChevronRight}
                            onClick={() => handleUserMessageSubmit()}
                            onKeyDown={(e) =>
                                e.key === "Enter" && handleUserMessageSubmit()
                            }
                        />
                    </Stack>
                </Stack>
            </Box>
        </Slide>
    );
};

const MessageCloud = (props: {
    side: "left" | "right";
    content: string;
}) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;

    const { side, content } = props;

    const borderRadius =
        side === "left" ? "4px 16px 16px 16px" : "16px 16px 4px 16px";
    const bgcolor =
        side === "left" ? alpha(primary, 0.08) : primary;
    const color = side === "left" ? "text.primary" : "white";

    return (
        <Stack
            sx={{
                px: 1.75,
                py: 1,
                bgcolor,
                borderRadius,
                maxWidth: "88%",
                boxShadow:
                    side === "right"
                        ? `0 1px 4px ${alpha(darken(primary, 0.3), 0.2)}`
                        : "none",
            }}
        >
            <Typography
                fontWeight={400}
                fontSize="14px"
                lineHeight="20px"
                color={color}
                sx={{ wordBreak: "break-word" }}
            >
                {content}
            </Typography>
        </Stack>
    );
};

const MessageBox = (props: { message: DisplayMessage; className?: string }) => {
    const { message, className } = props;

    const align = message.role === "assistant" ? "flex-start" : "flex-end";
    const side = message.role === "assistant" ? "left" : "right";
    const content = message.content?.toString() ?? "";

    return (
        <Stack
            width="90%"
            direction="row"
            justifyContent={align}
            className={className}
        >
            <Stack
                width="100%"
                direction="column"
                alignItems={align}
                spacing={0.5}
            >
                <MessageCloud side={side} content={content} />
            </Stack>
        </Stack>
    );
};

export default TextPromptModal;
